import { BuildException } from '@models/buildOrchestration';
import { MissionControlDataFlowEdge, MissionControlDataFlowNode, useMissionControlDataFlowData } from '@stores/data.store';
import { MouseEvent, forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useQueryParams } from '@services/url.service';
import Danger from '@components/statusIndicators/Danger.component';
import { MissionControlDataFlowDiagramManager } from '@components/missionControl/dataflow/MissionControlDataFlow';
import LoadingCard from '@components/card/LoadingCard.component';
import { getGroupValueForNodeType } from '@services/modeling.service';
import FullScreenTakeover from '@components/general/FullScreenTakeover.component';


const VisualTools = styled.div`
height: 40px;
box-shadow: 0px 2px 3px 0px #0000001A;
display: flex;

position: absolute;
top: 70px;
right: 20px;
z-index: 1001;
border: solid 1px var(--ct-border-color);
border-radius: 5px;
background-color: white;

div.spacer {
    display: inline-block;
    margin-left: 2rem;
    margin-right: 2rem;
    height: 38px;
    background-color: white;
    line-height: 38px;
    text-align: center;
    color: var(--ct-border-color);
}

button {
    height: 38px;
    width: 40px;
    line-height: 38px;
    text-align: center;
    background-color: white;
    color: #bbb;
    border: none;
    font-size: 20px;
    -webkit-transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, -webkit-box-shadow 0.15s ease-in-out;
    transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, -webkit-box-shadow 0.15s ease-in-out;

    &:disabled {
        color: var(--ct-border-color);
        cursor: default;
    }
    

    &.active {
        color: #00A1E0;

        &:hover {
            color: #00A1E0;
        }
    }

    &:hover {
        color: black;

        &:disabled {
            color: var(--ct-border-color);
        }

    }
    &:first-child {
        border-right: solid 1px var(--ct-border-color);
        border-top-left-radius: 5px;
        border-bottom-left-radius: 5px;
    }

    &:last-child {
        border-top-right-radius: 5px;
        border-bottom-right-radius: 5px;
    }
}
`

const EdgeActions = styled.div`

position: absolute;
width: 250px;
margin-left: calc(-490px);
z-index: 500;
display: flex;
flex-direction: row;
justify-content: center;


.button-wrapper {
    box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.3);
    border-radius: 5px;
}
`;

const EdgeButton = styled.button`
height: 40px;
font-family: 'POPPINS';
background: rgba(15, 14, 49, 1);
color: rgba(255, 255, 255, 1);
padding: 0px;
margin: 0px;
text-align: center;
font-size: 18px;
position: relative;
line-height: 32px;
padding: 4px 12px;
border: none;

&.drag-handle {
    padding: 4px 2px;
}

&:hover {
    cursor: pointer;
    background: rgba(15, 14, 49, .85);
}

&:first-child {
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
}

&:last-child {
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
}

&.confirm {
    background: #EA4335;
}
`;

const NodeImage = styled.div`

`

interface RelationshipError {
    relationshipId: string;
    parentObjectName: string;
    error: 'AMBIGUOUS' | 'ORPHAN';
}

interface BuildErrorProps {
    buildException?: BuildException;
    objectType: string;
    objectId: string;
}

const BuildError = (props: BuildErrorProps) => {

    if (props.buildException && props.buildException.error_type == 'MissingMappingException') {
        return <Danger>
            <div>
                <strong>Mapping Error</strong><br />
                <p>{props.buildException.message}</p>
                <Link className="btn btn-light" to={`/business-object/${props.objectId}/fields`}>Fix</Link>
            </div>
        </Danger>
    }

    return <Danger>
        <div>
            <strong>Unknown build error.</strong><br />
            <p>Sorry, something went wrong and we haven't done a good job telling you what it is! Please contact support if this error continues.</p>
        </div>
    </Danger>
}

interface HandleProps {
    color: string;
    x: number;
    y: number;
    zIndex: string;
}

const Handle = (props: HandleProps) => {
    return <>
        <circle  cx={props.x} cy={props.y} r="8" fill={props.color}/>
        <circle  cx={props.x} cy={props.y} r="7" fill="white"/>
        <circle  cx={props.x} cy={props.y} r="6" fill={props.color}/>
    </>;
}

// @ts-ignore
const MenuToggle = forwardRef(({ children, onClick }, ref) => (
    <button className="icon-button"
        style={{fontSize: '24px'}}
    //   @ts-ignore
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick(e);
      }}
    >
      {children}
    </button>
  ));


// @ts-ignore
const ConnectToggle = forwardRef(({ children, onClick, disabled }, ref) => (
    <button
    //   @ts-ignore
      ref={ref}
      disabled={disabled}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick(e);
      }}
    >
      {children}
    </button>
  ));


type edgeColor = 'default' | 'muted' | 'green' | 'purple' | 'red' | 'yellow';

interface EdgeProps {
    from: HTMLElement;
    to: HTMLElement;
    xOffset: number;
    yOffset: number;
    active: boolean;
    color: edgeColor;
    dashed?: boolean;
    onClick?: (evt: MouseEvent) => any;
    onHover?: (isHovering: boolean) => any;
    building?: boolean;
}

function calculateControlOffset(distance: number, curvature: number): number {
    if (distance >= 0) {
      return 0.5 * distance;
    }
  
    return curvature * 25 * Math.sqrt(-distance);
  }
  
  function getControlWithCurvature(pos: string, x1: number, y1: number, x2: number, y2: number, c: number): [number, number] {
    switch (pos) {
      case 'LEFT':
        return [x1 - calculateControlOffset(x1 - x2, c), y1];
      case 'RIGHT':
        return [x1 + calculateControlOffset(x2 - x1, c), y1];
    }
    throw new Error('invalid position');
  }



interface Position {
    x: number;
    y: number;
}

const Loader = styled.div`
position: fixed;
top: 50%;
left: 50%;
z-index: 2;
width: 100px;
height: 85px;
background: rgba(0, 0, 0, 0.5);
color: white;
border-radius: 10px;
line-height: 100px;
text-align: center;
`

// 


const CARD_MIN_WIDTH = 350;

export const recursiveGetNodesAndEdges = (
    thisNode: MissionControlDataFlowNode, 
    allNodes: MissionControlDataFlowNode[], 
    allEdges: MissionControlDataFlowEdge[], 
    nodeIdsWeSaw: string[], 
    edgeIdsWeSaw: string[],
    limitDirection?: string,
    excludeDescendentBusinessObjects?: boolean,
): [MissionControlDataFlowNode[], MissionControlDataFlowEdge[]] => {
    let nodes: MissionControlDataFlowNode[] = [];
    let edges: MissionControlDataFlowEdge[] = [];


    // Get edges pointing at this node
    allEdges.forEach(e => {
        if (e.source == thisNode.id && !nodeIdsWeSaw.includes(e.target) && (!limitDirection || limitDirection == 'source')) {
            const relatedNode = allNodes.find(n => n.id === e.target)!;
            if (!relatedNode) {
                return;
            }
            if (excludeDescendentBusinessObjects && getGroupValueForNodeType(thisNode.data.nodeType) == 'DATA_MODELING' && getGroupValueForNodeType(relatedNode.data.nodeType) == 'DATA_MODELING') {
                return;
            }
            nodes.push(relatedNode);
            edges.push(e);
            edgeIdsWeSaw.push(e.id);
            nodeIdsWeSaw.push(relatedNode.id);
            const [moreNodes, moreEdges] = recursiveGetNodesAndEdges(relatedNode, allNodes, allEdges, nodeIdsWeSaw, edgeIdsWeSaw, 'source', excludeDescendentBusinessObjects);
            moreNodes.forEach(n => nodeIdsWeSaw.push(n.id));
            moreEdges.forEach(e => edgeIdsWeSaw.push(e.id));

            nodes = nodes.concat(moreNodes);
            edges = edges.concat(moreEdges);
        } else if (e.target == thisNode.id && !nodeIdsWeSaw.includes(e.source) && (!limitDirection || limitDirection == 'target')) {
            const relatedNode = allNodes.find(n => n.id === e.source)!;
            if (!relatedNode) {
                return;
            }

            if (excludeDescendentBusinessObjects && getGroupValueForNodeType(thisNode.data.nodeType) == 'DATA_MODELING' && getGroupValueForNodeType(relatedNode.data.nodeType) == 'DATA_MODELING') {
                return;
            }
            nodes.push(relatedNode);
            edges.push(e);
            edgeIdsWeSaw.push(e.id);
            nodeIdsWeSaw.push(relatedNode.id);
            const [moreNodes, moreEdges] = recursiveGetNodesAndEdges(relatedNode, allNodes, allEdges, nodeIdsWeSaw, edgeIdsWeSaw, 'target', excludeDescendentBusinessObjects);
            moreNodes.forEach(n => nodeIdsWeSaw.push(n.id));
            moreEdges.forEach(e => edgeIdsWeSaw.push(e.id));

            nodes = nodes.concat(moreNodes);
            edges = edges.concat(moreEdges);
        } else if ((e.target == thisNode.id || e.source == thisNode.id) && !edgeIdsWeSaw.includes(e.id)) {
            edges.push(e);
            edgeIdsWeSaw.push(e.id);
        }
    });

    return [nodes, edges];


}

interface Props {
    show: boolean;
    mode: 'UPSTREAM' | 'DOWNSTREAM' | 'ALL';
    rootNodeId: string;
    onHide: () => any;
    nodeTitle?: string;
    excludeDescendentBusinessObjects?: boolean;
}

const DiagramStyles = styled.div`
border: solid 1px var(--ct-border-color);
border-radius: 5px;
flex: 1;
`

const DAG = (props: Props) => {

    const query = useQueryParams();
    const [currentFocusNode, setCurrentFocusNode] = useState(props.rootNodeId);

    useEffect(() => {
        setCurrentFocusNode(props.rootNodeId);
    }, [props.rootNodeId])

    const data = useMissionControlDataFlowData();


    const [edges, setEdges] = useState<MissionControlDataFlowEdge[]>([]);

    const mode = props.mode;


    const [filteredNodes, setFilteredNodes] = useState<MissionControlDataFlowNode[]>([]);
    useEffect(() => {
        if (!data.data) {
            setFilteredNodes([]);
            setEdges([]);
        } else {
            const theNode = currentFocusNode ? data.data.nodes.find(n => n.id === currentFocusNode) : undefined;
            if (theNode) {
                const limitDirection = mode == 'UPSTREAM' ? 'target' : mode == 'DOWNSTREAM' ? 'source' : undefined;
                const [nodesToUse, edgesToUse] = recursiveGetNodesAndEdges(theNode, data.data.nodes, data.data.edges, [currentFocusNode], [], limitDirection, props.excludeDescendentBusinessObjects);
                nodesToUse.push(theNode);
                setFilteredNodes(nodesToUse);
                setEdges(edgesToUse);
            } else {
                setFilteredNodes(data.data.nodes);
                setEdges(data.data.edges);
            }
        }
    }, [data.dataUpdatedAt, currentFocusNode, mode])
 
    const onClickNode = useCallback((nodeId: string) => {
    }, []);

    const toggleFocusOnNode = useCallback((nodeId: string) => {
        
    }, []);

    const nodeOptions = useMemo(() => {
        return filteredNodes.map(n => {
            return {
                label: n.data.title,
                value: n.id,
            }
        })
    }, [filteredNodes])

    return <FullScreenTakeover show={props.show} onHide={props.onHide} noCloseButton>
        <div className="p-4 d-flex flex-vertical h-100">
            <div className="d-flex center-vertically mb-2">
                <h2 className="mb-0 flex-1">
                    Data Flow: <span className="fw-light">Showing {props.mode == 'UPSTREAM' ? 'upstream' : props.mode == 'DOWNSTREAM' ? 'downstream' : 'all'} connections to</span> {props.nodeTitle}
                </h2>
                {/* <div>
                    <Dropdown
                        options={nodeOptions}
                        selected={currentFocusNode}
                        onChange={(newVal) => {
                            setCurrentFocusNode(newVal);
                        }}
                    />
                </div> */}
                <button className="btn btn-light" onClick={props.onHide}>
                    <i className="mdi mdi-undo"></i> Close
                </button>
            </div>
            <DiagramStyles>
                {data.isLoading && (
                    <LoadingCard></LoadingCard>
                )}
                {!data.isLoading && filteredNodes.length > 0 && (
                            <MissionControlDataFlowDiagramManager
                                nodeData={filteredNodes}
                                edgeData={edges}
                                onDeleteEdge={() => {

                                }}
                                onClickNode={onClickNode}
                                focusNodeId={currentFocusNode}
                                onDeleteNode={() => {

                                }}
                                onClickEdgeStatusIndicator={() => {
                                    
                                }}
                                onToggleNodeLineage={toggleFocusOnNode}
                                onNavigate={props.onHide}
                            />
                        
                    
                )}
            </DiagramStyles>
        </div>
    </FullScreenTakeover>
                    

                   
                       
               
        
}

export default DAG;

